import { ChevronRightIcon } from '@heroicons/react/20/solid'
import { Loader2 } from 'lucide-react'
import { useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'
import { useMutation, useQuery } from 'react-query'
import { useNavigate, useParams, useSearchParams } from 'react-router-dom'

import { yupResolver } from '@hookform/resolvers/yup'
import { toast } from 'sonner'
import * as yup from 'yup'

import { Button } from '@/components/ui/button'
import { Form } from '@/components/ui/form'
import { useAppDispatch, useAppSelector } from '@/hooks'
import { formatIban } from '@/lib/helper'
import { cn } from '@/lib/utils'
import { ContactForm } from '@/pages/customer/book-shelf/contact-form'
import { ExtrasForm } from '@/pages/customer/book-shelf/extras-form'
import { OverviewForm } from '@/pages/customer/book-shelf/overview-form'
import { PaketForm } from '@/pages/customer/book-shelf/paket-form'
import { RegalForm } from '@/pages/customer/book-shelf/regal-form'
import { Layout } from '@/pages/customer/layout'
import authService from '@/services/auth-service'
import storeService from '@/services/store-service'
import { setCurrentStore } from '@/slices/store'

import { BlendIcon, BlurIcon, CrownIcon, FlashIcon, TriangleIcon } from '@/assets/icons'
import Header from '@/components/header'
import standService from '@/services/stand-service'
import { format } from 'date-fns'

enum FormSteps {
	Paket = 0,
	Mietbeginn,
	Regal,
	Kontaktdaten,
	Extras,
	Übersicht,
}

export interface BookShelfForm {
	package: string
	startDate: Date
	endDate: Date
	lock_token?: string
	stand: Pick<StandStatus, 'id' | 'label' | 'x' | 'y' | 'is_available'>
	email: string
	firstName: string
	lastName: string
	country: string
	zip: string
	iban: string
	address: string
	phoneNumber: string
	agreeTerms: boolean
	privacyPolicy: boolean
	cancellationPolicy: boolean
	receiveNews: boolean
	password: string
	confirmPassword: string
	extras: string
	referralCode?: string
	couponCode?: string
}

export const pricing = [
	{
		id: 1,
		icon: BlendIcon,
		title: '1 Woche',
		pricing: 25,
		provision: 20,
	},
	{
		id: 2,
		icon: FlashIcon,
		title: '2 Wochen',
		pricing: 40,
		provision: 20,
	},
	{
		id: 3,
		icon: TriangleIcon,
		title: '3 Wochen',
		pricing: 60,
		provision: 20,
	},
	{
		id: 4,
		icon: BlurIcon,
		title: '4 Wochen',
		pricing: 70,
		provision: 20,
	},
	{
		id: 5,
		icon: CrownIcon,
		title: '4 Wochen',
		pricing: 0,
		provision: 45,
	},
]

export const BookShelf = () => {
	const auth = useAppSelector(state => state.auth)
	const currentStore = useAppSelector(state => state.store.current)
	const dispatch = useAppDispatch()
	const navigate = useNavigate()
	const { storeRef = '' } = useParams()
	const [searchParams] = useSearchParams()

	const [currentStep, setCurrentStep] = useState<FormSteps>(
		searchParams.get('paket') ? FormSteps.Regal : FormSteps.Paket,
	)
	const [couponDetail, setCouponDetail] = useState<Coupon | null>(null)
	const [referralDetail, setReferralDetail] = useState<Referral | null>(null)

	const [lockExpiry, setLockExpiry] = useState<Date | null>(null)
	const [timeLeft, setTimeLeft] = useState<string>('')

	const {
		data: bookingStore,
		isLoading: isStoreLoading,
		error: storeError,
	} = useQuery(['public-store', storeRef], () => storeService.getPublicStore(storeRef), {
		enabled: !!storeRef,
		refetchOnWindowFocus: false,
		onSuccess: data => {
			dispatch(
				setCurrentStore({
					uuid: data.uuid,
					slug: data.slug,
					name: data.name,
					logo_url: data.logo_url,
					tagline: data.tagline,
					city: data.city,
					zip: data.zip,
					address: data.address,
					phone: data.phone,
					email: data.email,
					instagram: data.instagram,
					working_hours: data.working_hours,
					paypal_client_id: data.paypal_client_id,
					paypal_mode: data.paypal_mode,
				}),
			)
		},
		onError: (err: any) => {
			toast.error(err?.response?.data?.message || 'Store nicht gefunden')
		},
	})

	const activeStore = bookingStore
		? {
				uuid: bookingStore.uuid,
				slug: bookingStore.slug,
				name: bookingStore.name,
				logo_url: bookingStore.logo_url,
			}
		: currentStore

	useEffect(() => {
		if (!storeRef && !currentStore?.uuid) {
			navigate('/', { replace: true })
		}
	}, [storeRef, currentStore?.uuid, navigate])

	useEffect(() => {
		if (!lockExpiry) return
		const interval = setInterval(() => {
			const now = new Date()
			const diff = lockExpiry.getTime() - now.getTime()
			if (diff <= 0) {
				clearInterval(interval)
				const storePath = activeStore?.slug || activeStore?.uuid
				window.location.href = storePath ? `/stores/${storePath}` : '/'
				return
			}
			const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60))
			const seconds = Math.floor((diff % (1000 * 60)) / 1000)
			setTimeLeft(`${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`)
		}, 1000)
		return () => clearInterval(interval)
	}, [lockExpiry, activeStore?.slug, activeStore?.uuid])

	const { mutate: verifyEmail } = useMutation({
		mutationKey: 'verify-email',
		mutationFn: (data: { email: string }) => authService.verifyEmail(data),
	})

	const { mutate: lockStand } = useMutation({
		mutationKey: 'lock-stand',
		mutationFn: ({ stand_id, start, end }: { stand_id: Stand['id']; start: string; end: string }) =>
			standService.lockStand(stand_id, start, end),
	})

	const schema = yup.object<BookShelfForm>().shape({
		package: yup.string().when('$step', {
			is: FormSteps.Paket,
			then: schema => schema.required('Please select a package'),
		}),
		startDate: yup.date().when('$step', {
			is: FormSteps.Regal,
			then: schema => schema.required('Please select start date'),
		}),
		endDate: yup.date().when('$step', {
			is: FormSteps.Regal,
			then: schema => schema.required('Please select end date'),
		}),
		stand: yup.object<BookShelfForm['stand']>().when('$step', {
			is: FormSteps.Regal,
			then: schema => schema.required('Please select a stand to proceed'),
		}),
		firstName: yup.string().when('$step', {
			is: FormSteps.Kontaktdaten,
			then: schema => schema.required('Name ist erforderlich!'),
			otherwise: schema => schema.notRequired(),
		}),
		lastName: yup.string().when('$step', {
			is: FormSteps.Kontaktdaten,
			then: schema => schema.required('Name ist erforderlich!'),
			otherwise: schema => schema.notRequired(),
		}),
		email: yup.string().when('$step', {
			is: FormSteps.Kontaktdaten,
			then: schema =>
				schema
					.required('E-Mail ist erforderlich')
					.email('Geben Sie eine gültige E-Mail-Adresse ein'),
			otherwise: schema => schema.notRequired(),
		}),
		country: yup.string().when('$step', {
			is: FormSteps.Kontaktdaten,
			then: schema => schema.required('Ort ist erforderlich!'),
			otherwise: schema => schema.notRequired(),
		}),
		zip: yup.string().when('$step', {
			is: FormSteps.Kontaktdaten,
			then: schema => schema.required('Postleitzahl ist erforderlich!'),
			otherwise: schema => schema.notRequired(),
		}),
		iban: yup.string().when('$step', {
			is: FormSteps.Kontaktdaten,
			then: schema =>
				schema
					.required('IBAN ist erforderlich!')
					.matches(/^DE(\s?\d){20}$/, "IBAN muss mit 'DE' beginnen und genau 20 Zahlen enthalten"),
			otherwise: schema => schema.notRequired(),
		}),
		address: yup.string().when('$step', {
			is: FormSteps.Kontaktdaten,
			then: schema => schema.required('Adresse ist erforderlich!'),
			otherwise: schema => schema.notRequired(),
		}),
		phoneNumber: yup.string().when('$step', {
			is: FormSteps.Kontaktdaten,
			then: schema => schema.required('Telefonnummer ist erforderlich!'),
			otherwise: schema => schema.notRequired(),
		}),
		agreeTerms: yup.bool().when('$step', {
			is: FormSteps.Kontaktdaten,
			then: schema =>
				schema
					.required('Bitte stimmen Sie den Allgemeinen Geschäftsbedingungen zu')
					.oneOf([true], 'Bitte stimmen Sie den Allgemeinen Geschäftsbedingungen zu'),
		}),
		privacyPolicy: yup.bool().when('$step', {
			is: FormSteps.Kontaktdaten,
			then: schema =>
				schema
					.required('Bitte akzeptieren Sie die Datenschutzrichtlinie')
					.oneOf([true], 'Bitte akzeptieren Sie die Datenschutzrichtlinie'),
		}),
		receiveNews: yup.bool(),
		cancellationPolicy: yup.bool().when('$step', {
			is: FormSteps.Kontaktdaten,
			then: schema =>
				schema
					.required('Bitte akzeptieren Sie die Stornierungsbedingungen')
					.oneOf([true], 'Bitte akzeptieren Sie die Stornierungsbedingungen'),
		}),
		referralCode: yup.string().notRequired(),
		couponCode: yup.string().notRequired(),
		extras: yup.string().when('$step', {
			is: FormSteps.Extras,
			then: schema => schema.required('Please select a package'),
		}),
	})

	const form = useForm<BookShelfForm>({
		resolver: yupResolver(schema as any),
		defaultValues: {
			package: searchParams.get('paket') as string,
			firstName: auth?.first_name,
			lastName: auth?.last_name,
			email: auth?.email,
			address: auth?.address ?? '',
			zip: auth?.zip,
			iban: auth?.iban ? formatIban(auth.iban) : 'DE',
			country: auth?.country,
			phoneNumber: auth?.phone_number ?? '',
			agreeTerms: false,
			privacyPolicy: false,
			receiveNews: false,
			cancellationPolicy: false,
		},
		context: { step: currentStep },
		mode: 'all',
	})

	const formatLocalDate = (date: Date) => {
		const year = date.getFullYear()
		const month = `${date.getMonth() + 1}`.padStart(2, '0')
		const day = `${date.getDate()}`.padStart(2, '0')
		return `${year}-${month}-${day}`
	}

	const goToPrevStep = () => {
		if (currentStep === FormSteps.Regal) {
			setCurrentStep(prev => prev - 2)
		} else {
			setCurrentStep(prev => prev - 1)
		}
		window.scrollTo(0, 0)
	}

	const goToNextStep = form.handleSubmit(
		() => {
			if (currentStep === FormSteps.Paket) setCurrentStep(prev => prev + 2)
			else if (currentStep === FormSteps.Kontaktdaten && !auth?.id) {
				verifyEmail(
					{ email: form.watch('email') as string },
					{
						onSuccess: res => {
							if (res?.message === 'User exist with this email') {
								toast.error(
									'Ihre E-Mail existiert bereits. Bitte loggen Sie sich in Ihr Konto ein und versuchen Sie es erneut.',
								)
							} else {
								setCurrentStep(prev => prev + 1)
							}
						},
					},
				)
			} else if (currentStep === FormSteps.Regal) {
				const standId = form.getValues('stand.id')
				const startDate = formatLocalDate(
					new Date(format(form.getValues('startDate'), 'yyyy-MM-dd')),
				)
				const endDate = formatLocalDate(new Date(format(form.getValues('endDate'), 'yyyy-MM-dd')))

				lockStand(
					{ stand_id: standId, start: startDate, end: endDate },
					{
						onSuccess: res => {
							form.setValue('lock_token', res?.lockToken)
							const localExpiry = new Date(res?.expiresAt)
							setLockExpiry(localExpiry)
							setCurrentStep(prev => prev + 1)
						},
						onError: (err: any) => {
							toast.error(err?.response?.data?.message)
						},
					},
				)
			} else {
				setCurrentStep(prev => prev + 1)
			}
			window.scrollTo(0, 0)
		},
		errors => {
			if (errors.package) {
				toast.error(errors.package?.message)
			}
			if (errors.stand) {
				toast.error(errors.stand?.message)
			}
		},
	)

	if (storeRef && isStoreLoading) {
		return (
			<div className="min-h-screen flex items-center justify-center bg-[#F7F8F8]">
				<Loader2 className="animate-spin text-secondary" />
			</div>
		)
	}

	if ((storeRef && (storeError || !bookingStore)) || !activeStore?.uuid) {
		return (
			<div className="min-h-screen flex flex-col items-center justify-center gap-3 bg-[#F7F8F8] px-5">
				<p className="text-secondary font-bold">Store nicht gefunden</p>
				<Button type="button" onClick={() => navigate('/')}>
					Zur Store-Suche
				</Button>
			</div>
		)
	}

	const wizardTitle = activeStore.name
		? `Regal mieten · ${activeStore.name}`
		: 'Regal Mieten'

	return (
		<>
			{auth?.id ? (
				<Layout title={wizardTitle}>
					<div className="mt-2 lg:mt-3 grow container mx-auto rounded-xl lg:rounded-[16px] px-4 max-lg:pb-3 max-lg:pt-8 lg:px-6 lg:py-10 bg-white flex flex-col">
						<p className="text-sm text-muted-foreground mb-3 lg:mb-4">
							Buchung bei <span className="font-semibold text-secondary">{activeStore.name}</span>
						</p>
						<ProgressBar
							steps={Object.keys(FormSteps).filter(key => isNaN(Number(key)))}
							currentStep={currentStep}
						/>
						{lockExpiry && (
							<div className="text-center mb-0 mt-5">
								<p className="text-red-600 font-semibold">
									Dieses Regal ist noch {timeLeft} Minuten für dich geblockt
								</p>
							</div>
						)}
						<Form {...form}>
							<form
								className="flex flex-col grow gap-12 justify-between"
								onSubmit={event => goToNextStep(event)}>
								{currentStep === FormSteps.Paket && (
									<PaketForm onSelection={() => setCurrentStep(FormSteps.Regal)} />
								)}
								{currentStep === FormSteps.Regal && <RegalForm />}
								{currentStep === FormSteps.Kontaktdaten && (
									<ContactForm
										couponDetail={couponDetail}
										referralDetail={referralDetail}
										setCouponDetail={setCouponDetail}
										setReferralDetail={setReferralDetail}
									/>
								)}
								{currentStep === FormSteps.Extras && <ExtrasForm />}
								{currentStep === FormSteps.Übersicht && (
									<OverviewForm couponDetail={couponDetail} referralDetail={referralDetail} />
								)}

								<div className="flex items-center justify-center lg:justify-end gap-2 lg:gap-3">
									{currentStep > 0 && (
										<Button
											variant="outline"
											type="button"
											onClick={goToPrevStep}
											className="rounded-xl max-lg:flex-1 w-[144px] lg:w-[194px] h-10 lg:h-12 border-secondary text-secondary font-extrabold">
											Zurück
										</Button>
									)}
									{currentStep !== FormSteps.Übersicht && (
										<Button
											variant="secondary"
											className="rounded-xl max-lg:flex-1 w-[144px] lg:w-[194px] h-10 lg:h-12 flex items-center font-extrabold">
											Weiter
											<ChevronRightIcon className="size-4 shrink-0" />
										</Button>
									)}
								</div>
							</form>
						</Form>
					</div>
				</Layout>
			) : (
				<div className="mx-auto">
					<Header currentPage="regalMieten" store={activeStore} />
					<div className="mt-2 lg:mt-3 grow container mx-auto rounded-xl lg:rounded-[16px] px-4 max-lg:pb-3 max-lg:pt-8 lg:px-6 lg:py-10 bg-white flex flex-col">
						<p className="text-sm text-muted-foreground mb-3 lg:mb-4">
							Buchung bei <span className="font-semibold text-secondary">{activeStore.name}</span>
						</p>
						<ProgressBar
							steps={Object.keys(FormSteps).filter(key => isNaN(Number(key)))}
							currentStep={currentStep}
						/>
						{lockExpiry && (
							<div className="text-center mt-10">
								<p className="text-red-600 font-semibold">
									Dieses Regal ist noch {timeLeft} Minuten für dich geblockt
								</p>
							</div>
						)}
						<Form {...form}>
							<form
								className="flex flex-col max-lg:mx-0 lg:mx-10 grow gap-8 lg:gap-12 justify-between"
								onSubmit={event => goToNextStep(event)}>
								{currentStep === FormSteps.Paket && (
									<PaketForm onSelection={() => setCurrentStep(FormSteps.Regal)} />
								)}
								{currentStep === FormSteps.Regal && <RegalForm />}
								{currentStep === FormSteps.Kontaktdaten && (
									<ContactForm
										couponDetail={couponDetail}
										referralDetail={referralDetail}
										setCouponDetail={setCouponDetail}
										setReferralDetail={setReferralDetail}
									/>
								)}
								{currentStep === FormSteps.Extras && <ExtrasForm />}
								{currentStep === FormSteps.Übersicht && (
									<OverviewForm couponDetail={couponDetail} referralDetail={referralDetail} />
								)}

								<div className="flex items-center justify-center lg:justify-end gap-2 lg:gap-3">
									{currentStep > 0 && (
										<Button
											variant="outline"
											type="button"
											onClick={goToPrevStep}
											className="rounded-xl max-lg:flex-1 w-[144px] lg:w-[194px] h-10 lg:h-12 border-secondary text-secondary font-extrabold">
											Zurück
										</Button>
									)}
									{currentStep !== FormSteps.Übersicht && (
										<Button
											variant="secondary"
											className="rounded-xl max-lg:flex-1 w-[144px] lg:w-[194px] h-10 lg:h-12 flex items-center font-extrabold">
											Weiter
											<ChevronRightIcon className="size-4 shrink-0" />
										</Button>
									)}
								</div>
							</form>
						</Form>
					</div>
				</div>
			)}
		</>
	)
}

interface ProgressBarProps {
	currentStep: number
	steps: string[]
}

export const ProgressBar = ({ steps, currentStep }: ProgressBarProps) => {
	return (
		<div className="flex overflow-x-auto max-lg:pb-3 lg:items-center lg:justify-center gap-2 lg:gap-4">
			{steps.map((step, index) => (
				<div key={index} className="flex items-center justify-center gap-2 lg:gap-4">
					<div className="flex items-center gap-2">
						<div
							className={cn(
								'size-[22px] lg:size-8 flex items-center justify-center border border-secondary rounded-full',
								currentStep >= index ? 'bg-secondary' : 'bg-white',
							)}>
							<p
								className={cn(
									'font-medium max-lg:text-xs',
									currentStep >= index ? 'text-white' : 'text-secondary',
								)}>
								{index + 1}
							</p>
						</div>
						<h2
							className={cn(
								'font-medium whitespace-nowrap max-lg:text-xs',
								currentStep >= index ? 'text-secondary' : 'text-secondary/50',
							)}>
							{step}
						</h2>
					</div>
					{index < steps.length - 1 && <ChevronRightIcon className="size-3 lg:size-4" />}
				</div>
			))}
		</div>
	)
}
